import { appStakeUnstakePopupState } from "@/atoms/appPopupState"
import { restakingVaultState } from "@/atoms/restakingState"
import { Stat } from "@/components/stat"
import useTrans from "@/hooks/useTrans"
import { DAYS_IN_YEAR, SECONDS_IN_DAY, SKELETON_VALUE, STAKING } from "@/utils/constants"
import { RestakingLockupOptions } from "@/utils/dataTypes"
import { StyleObject, isValidNumber } from "@/utils/helpers"
import { formatNumberToDigits, formatPercent, formatToken } from "@/utils/numberFormatter"
import { Box, Stack, Typography } from "@mui/material"
import { useAtomValue } from "jotai"
import { FC, useMemo } from "react"
import * as styles from "./styles"
import { StakingSelectItem } from "@/components/staking/components/detail/types"

type BorrowRepayModalStatProps = {
  amount?: number
  selectedStakingItem: StakingSelectItem
}

type StakingTitleKeys = "estimateReward" | "stakingAPY"
type UnstakingTitleKeys = "estimateCooldown"

type StakeTitleStat = {
  [key in StakingTitleKeys]: React.ReactNode
}

type UnstakingTitleStat = {
  [key in UnstakingTitleKeys]: React.ReactNode
}

type StakingStatPropsWithKey = {
  titleKey: StakingTitleKeys
  content: React.ReactNode
  contentStyle?: StyleObject
  statsWrapper?: StyleObject
  statsTitleWrapper?: StyleObject
}

type UnstakingStatPropsWithKey = {
  titleKey: UnstakingTitleKeys
  content: React.ReactNode
  contentStyle?: StyleObject
  statsWrapper?: StyleObject
  statsTitleWrapper?: StyleObject
}

const checkDuration = (amount: number, options: RestakingLockupOptions[]): number | null => {
  for (const optionInfo of options) {
    const { minUnstakingAmount, duration } = optionInfo
    if (amount >= minUnstakingAmount) {
      return duration
    }
  }
  return null // Return null if the amount doesn't meet any duration
}

const StakeUnstakeModalStat: FC<BorrowRepayModalStatProps> = ({ amount, selectedStakingItem }) => {
  const trans = useTrans()
  const restakingVault = useAtomValue(restakingVaultState)
  const popupState = useAtomValue(appStakeUnstakePopupState)

  const lockupDuration = checkDuration(amount ?? 0, restakingVault?.lockupOptions ?? [])

  // stake
  const stakingTitleEls: StakeTitleStat = useMemo(
    () => ({
      estimateReward: (
        <Typography className='text-neutral-600 text-xs sm:text-sm'>{trans.staking.modal.estimate_reward}</Typography>
      ),
      stakingAPY: (
        <Typography className='text-neutral-600 text-xs sm:text-sm'>{trans.staking.modal.staking_apy}</Typography>
      ),
    }),
    [trans],
  )

  const stakingAPYPerday = selectedStakingItem.apy / DAYS_IN_YEAR
  const stakingStats: StakingStatPropsWithKey[] = [
    {
      titleKey: "estimateReward",
      content: (
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          spacing={0.5}
          className='text-neutral-1000 text-xs sm:text-sm font-semibold'
        >
          <Typography component='span'>
            {isValidNumber(stakingAPYPerday) ? formatToken(stakingAPYPerday * (amount ?? 0)) : SKELETON_VALUE}
          </Typography>
          <Typography component='span'>{selectedStakingItem.rewardTokenSymbol}</Typography>
        </Stack>
      ),
    },
    {
      titleKey: "stakingAPY",
      content: isValidNumber(stakingAPYPerday) ? formatPercent(selectedStakingItem.apy) : SKELETON_VALUE,
      contentStyle: styles.stakingAPYText,
    },
  ]

  // unstake
  const unstakingTitleEls: UnstakingTitleStat = useMemo(
    () => ({
      estimateCooldown: (
        <>
          <Typography className='text-neutral-600 text-xs sm:text-sm'>
            {trans.staking.modal.estimate_cooldown}
          </Typography>
        </>
      ),
    }),
    [trans],
  )

  const unstakingStats: UnstakingStatPropsWithKey[] = [
    {
      titleKey: "estimateCooldown",
      content: (
        <Box sx={styles.estimateWrapper}>
          <Typography sx={styles.estimateAmount} component='span'>
            {restakingVault && lockupDuration ? formatNumberToDigits(lockupDuration / SECONDS_IN_DAY) : 0}
          </Typography>{" "}
          <Typography sx={styles.estimateText} component='span'>
            {trans.staking.modal.days}
          </Typography>
        </Box>
      ),
    },
  ]

  return (
    <Stack className='bg-gradient-box-status rounded-lg border border-solid border-secondary p-3 sm:px-6 sm:py-3'>
      {popupState.type === STAKING
        ? stakingStats.map((stat, index) => (
            <Stat
              key={`${stat.titleKey}-${index}`}
              titleEl={stakingTitleEls[stat.titleKey]}
              content={stat.content}
              contentStyle={stat.contentStyle}
            />
          ))
        : unstakingStats.map((stat, index) => (
            <Stat
              key={`${stat.titleKey}-${index}`}
              titleEl={unstakingTitleEls[stat.titleKey]}
              content={stat.content}
              contentStyle={stat.contentStyle}
            />
          ))}
    </Stack>
  )
}

export default StakeUnstakeModalStat
